import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { 
  Users, 
  Plus, 
  Search, 
  User as UserIcon, 
  UserPlus,
  RefreshCw,
  Shield,
  Upload,
} from 'lucide-react';
import { userManagementService, User, UserCreateData, UserListParams, UserStatistics } from '../../services/userManagementService';
import { useNotification } from '../../hooks/useNotification';
import { useAppSelector } from '../../store/hooks';
import Button from '../../components/UI/Button';
import { logError } from '../../utils/errorLogger';
import { formatFullNameWithTitle } from '../../utils/guestNameUtils';
import Modal from '../../components/UI/Modal';
import ConfirmDeleteModal from '../../components/UI/ConfirmDeleteModal';
import RestoreUserModal, { DeletedUserInfo } from '../../components/UI/RestoreUserModal';
import UserForm from '../../components/Users/UserForm';
import UserTableView from '../../components/Users/UserTableView';
import UserGridView from '../../components/Users/UserGridView';
import GuestDetailsModal from '../../components/Users/GuestDetailsModal';
import EnhancedLightCard from '../../components/UI/EnhancedLightCard';
import BulkUserUpload from '../../components/Users/BulkUserUpload';
import HistoricalEventImport from '../../components/Users/HistoricalEventImport';
import ViewToggle, { ViewMode } from '../../components/UI/ViewToggle';
import Pagination from '../../components/UI/Pagination';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import Select from '../../components/UI/Select';

const UserManagementPage: React.FC = () => {
  const { showNotification } = useNotification();
  const { theme } = useAppSelector((state) => state.ui);
  
  // State management
  const [users, setUsers] = useState<User[]>([]);
  const [statistics, setStatistics] = useState<UserStatistics['data'] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize] = useState(20);
  
  // Filter and search state
  const [searchTerm, setSearchTerm] = useState('');
  
  // Debug searchTerm state changes
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [sortBy, setSortBy] = useState<string>('-created_at');
  const [isRoleDropdownOpen, setIsRoleDropdownOpen] = useState(false);
  const roleDropdownRef = useRef<HTMLDivElement>(null);
  
  // Modal state
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isBulkUploadModalOpen, setIsBulkUploadModalOpen] = useState(false);
  const [isHistoricalEventImportModalOpen, setIsHistoricalEventImportModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isGuestDetailsModalOpen, setIsGuestDetailsModalOpen] = useState(false);
  const [isRestoreModalOpen, setIsRestoreModalOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [guestForDetails, setGuestForDetails] = useState<User | null>(null);
  const [deletedUserInfo, setDeletedUserInfo] = useState<DeletedUserInfo | null>(null);
  const [pendingUserData, setPendingUserData] = useState<UserCreateData | null>(null);
  
  // Action loading states
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});
  
  // View state - default to grid for mobile, table for desktop
  const [currentView, setCurrentView] = useState<ViewMode>(() => {
    // Check if we're on mobile (screen width < 640px)
    if (typeof window !== 'undefined') {
      return window.innerWidth < 640 ? 'grid' : 'table';
    }
    return 'grid'; // Default to grid for SSR
  });

  // Update view based on screen size
  useEffect(() => {
    const handleResize = () => {
      const isMobile = window.innerWidth < 640;
      setCurrentView(prevView => {
        if (isMobile) {
          // Mobile: always use grid view
          return 'grid';
        } else {
          // Desktop: if currently on grid (from mobile), switch to table
          // Otherwise keep the current view
          return prevView === 'grid' ? 'table' : prevView;
        }
      });
    };

    window.addEventListener('resize', handleResize);
    // Set initial view
    handleResize();

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Close role dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (roleDropdownRef.current && !roleDropdownRef.current.contains(event.target as Node)) {
        setIsRoleDropdownOpen(false);
      }
    };

    if (isRoleDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isRoleDropdownOpen]);

  // Load users data
  const loadUsers = useCallback(async (params: UserListParams = {}) => {
    try {
      setLoading(true);
      setError(null);
      
      const queryParams: UserListParams = {
        page: currentPage,
        page_size: pageSize,
        ordering: sortBy,
        ...params
      };
      
      // Apply filters - support multiple roles
      if (selectedRoles.length > 0) {
        queryParams.roles = selectedRoles;
      }
      if (statusFilter !== 'all') {
        queryParams.is_active = statusFilter === 'active';
      }
      
      // Use only the search parameter to avoid React closure issues
      if (params.search) {
        queryParams.search = params.search;
      }
      
      const response = await userManagementService.getUsers(queryParams);
      
      if (response.success) {
        setUsers(response.data.users);
        setTotalPages(response.data.pagination.total_pages);
        setTotalCount(response.data.pagination.total_count);
      } else {
        setError('Failed to load users');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load users');
      showNotification('Error loading users', 'error');
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, selectedRoles, statusFilter, sortBy, showNotification]);

  // Load statistics
  const loadStatistics = useCallback(async () => {
    try {
      const response = await userManagementService.getUserStatistics();
      if (response.success) {
        setStatistics(response.data);
      }
    } catch (err) {
      logError('Failed to load statistics', err);
    }
  }, []);

  // Initial load
  useEffect(() => {
    loadUsers();
    loadStatistics();
  }, [loadUsers, loadStatistics]);

  // Handle search with improved debounce (faster response)
    useEffect(() => {
      const timeoutId = setTimeout(() => {
        if (searchTerm !== '') {
          loadUsers({ search: searchTerm });
        } else {
          loadUsers();
        }
      }, 300);

      return () => clearTimeout(timeoutId);
    }, [searchTerm, loadUsers]);
  

  // Handle user creation
  const handleCreateUser = async (userData: UserCreateData | Partial<UserCreateData>) => {
    try {
      setActionLoading('create');
      setServerErrors({}); // Clear previous errors
      const response = await userManagementService.createUser(userData as UserCreateData);
      
      // Check if a deleted user exists and requires confirmation
      if (!response.success && response.requires_confirmation && response.deleted_user) {
        setDeletedUserInfo(response.deleted_user);
        setPendingUserData(userData as UserCreateData);
        setIsRestoreModalOpen(true);
        setActionLoading(null);
        return;
      }
      
      if (response.success) {
        const message = response.restored 
          ? 'User restored and updated successfully' 
          : 'User created successfully';
        showNotification(message, 'success');
        setIsCreateModalOpen(false);
        setServerErrors({});
        loadUsers();
        loadStatistics();
      } else {
        // Handle validation errors
        if (response.error?.details && typeof response.error.details === 'object') {
          // Convert array errors to strings (DRF returns errors as arrays)
          const formattedErrors: Record<string, string> = {};
          Object.entries(response.error.details).forEach(([key, value]) => {
            if (Array.isArray(value)) {
              formattedErrors[key] = value[0] || String(value);
            } else if (typeof value === 'string') {
              formattedErrors[key] = value;
            } else {
              formattedErrors[key] = String(value);
            }
          });
          setServerErrors(formattedErrors);
          // Don't show notification for validation errors - they're displayed in the form
        } else {
          showNotification(response.error?.message || 'Failed to create user', 'error');
        }
      }
    } catch (err: any) {
      // Handle axios error responses
      // Check multiple possible error response structures
      let errorDetails: Record<string, string> | null = null;
      
      // Try different error response formats
      if (err.response?.data?.error?.details) {
        errorDetails = err.response.data.error.details;
      } else if (err.response?.data?.details) {
        errorDetails = err.response.data.details;
      } else if (err.response?.data?.error && typeof err.response.data.error === 'object') {
        // If error is an object, check if it has field-specific errors
        const errorObj = err.response.data.error;
        if (errorObj.email || errorObj.phone || errorObj.password || errorObj.roles) {
          errorDetails = errorObj;
        }
      }
      
      // Also check if serializer.errors format (Django REST framework format)
      if (!errorDetails && err.response?.data) {
        const data = err.response.data;
        // Check for DRF format: { "email": ["Email already exists"] }
        if (data.email && Array.isArray(data.email)) {
          errorDetails = { email: data.email[0] };
        } else if (data.phone && Array.isArray(data.phone)) {
          errorDetails = { phone: data.phone[0] };
        } else if (typeof data === 'object' && !data.success && !data.error) {
          // If data is an object with field names directly (DRF validation errors)
          // Check if it looks like field errors (has common field names)
          const commonFields = ['email', 'phone', 'password', 'password_confirm', 'first_name', 'last_name', 'roles', 'role'];
          const hasFieldErrors = commonFields.some(field => data[field]);
          if (hasFieldErrors) {
            errorDetails = data;
          }
        }
      }
      
      if (errorDetails && typeof errorDetails === 'object' && Object.keys(errorDetails).length > 0) {
        // Convert array errors to strings if needed
        const formattedErrors: Record<string, string> = {};
        Object.entries(errorDetails).forEach(([key, value]) => {
          if (Array.isArray(value)) {
            formattedErrors[key] = value[0] || String(value);
          } else if (typeof value === 'string') {
            formattedErrors[key] = value;
          } else {
            formattedErrors[key] = String(value);
          }
        });
        setServerErrors(formattedErrors);
        // Don't show notification for validation errors - they're displayed in the form
      } else if (err.response?.data?.error?.message) {
        // If there's a general error message but no field-specific details
        showNotification(err.response.data.error.message || 'Failed to create user', 'error');
      } else {
        showNotification(err.message || 'Failed to create user', 'error');
      }
    } finally {
      setActionLoading(null);
    }
  };

  // Handle user update
  const handleUpdateUser = async (userData: Partial<UserCreateData>) => {
    if (!selectedUser) return;
    
    try {
      setActionLoading('update');
      setServerErrors({}); // Clear previous errors
      const response = await userManagementService.partialUpdateUser(selectedUser.id, userData);
      
      if (response.success) {
        showNotification('User updated successfully', 'success');
        setIsEditModalOpen(false);
        setSelectedUser(null);
        setServerErrors({});
        loadUsers();
        loadStatistics();
      } else {
        // Handle validation errors
        if (response.error?.details && typeof response.error.details === 'object') {
          // Convert array errors to strings (DRF returns errors as arrays)
          const formattedErrors: Record<string, string> = {};
          Object.entries(response.error.details).forEach(([key, value]) => {
            if (Array.isArray(value)) {
              formattedErrors[key] = value[0] || String(value);
            } else if (typeof value === 'string') {
              formattedErrors[key] = value;
            } else {
              formattedErrors[key] = String(value);
            }
          });
          setServerErrors(formattedErrors);
          // Don't show notification for validation errors - they're displayed in the form
        } else {
          showNotification(response.error?.message || 'Failed to update user', 'error');
        }
      }
    } catch (err: any) {
      // Handle axios error responses
      // Check multiple possible error response structures
      let errorDetails: Record<string, string> | null = null;
      
      // Try different error response formats
      if (err.response?.data?.error?.details) {
        errorDetails = err.response.data.error.details;
      } else if (err.response?.data?.details) {
        errorDetails = err.response.data.details;
      } else if (err.response?.data?.error && typeof err.response.data.error === 'object') {
        // If error is an object, check if it has field-specific errors
        const errorObj = err.response.data.error;
        if (errorObj.email || errorObj.phone || errorObj.password) {
          errorDetails = errorObj;
        }
      }
      
      // Also check if serializer.errors format (Django REST framework format)
      if (!errorDetails && err.response?.data) {
        const data = err.response.data;
        // Check for DRF format: { "email": ["Email already exists"] }
        if (data.email && Array.isArray(data.email)) {
          errorDetails = { email: data.email[0] };
        } else if (data.phone && Array.isArray(data.phone)) {
          errorDetails = { phone: data.phone[0] };
        } else if (typeof data === 'object' && !data.success && !data.error) {
          // If data is an object with field names directly
          errorDetails = data;
        }
      }
      
      if (errorDetails && typeof errorDetails === 'object' && Object.keys(errorDetails).length > 0) {
        // Convert array errors to strings if needed
        const formattedErrors: Record<string, string> = {};
        Object.entries(errorDetails).forEach(([key, value]) => {
          if (Array.isArray(value)) {
            formattedErrors[key] = value[0] || String(value);
          } else if (typeof value === 'string') {
            formattedErrors[key] = value;
          } else {
            formattedErrors[key] = String(value);
          }
        });
        setServerErrors(formattedErrors);
        // Don't show notification for validation errors - they're displayed in the form
      } else if (err.response?.data?.error?.message) {
        // If there's a general error message but no field-specific details
        showNotification(err.response.data.error.message || 'Failed to update user', 'error');
      } else {
        showNotification(err.message || 'Failed to update user', 'error');
      }
    } finally {
      setActionLoading(null);
    }
  };

  // Handle user deletion - show confirmation modal
  const handleDeleteUser = (user: User) => {
    setUserToDelete(user);
    setIsDeleteModalOpen(true);
  };

  // Handle showing guest details
  const handleShowGuestDetails = (user: User) => {
    setGuestForDetails(user);
    setIsGuestDetailsModalOpen(true);
  };

  // Confirm user deletion
  const confirmDeleteUser = async () => {
    if (!userToDelete) return;
    
    try {
      setActionLoading(userToDelete.id);
      const response = await userManagementService.deleteUser(userToDelete.id);
      
      if (response.success) {
        // Close modal and refresh data without showing notification
        setIsDeleteModalOpen(false);
        setUserToDelete(null);
        loadUsers();
        loadStatistics();
      } else {
        showNotification(response.error?.message || 'Failed to delete user', 'error');
      }
    } catch (err: any) {
      // Check if it's a network error but the user was actually deleted
      if (err.code === 'ERR_NETWORK' || err.message === 'Network Error') {
        // Try to refresh the users list to see if the user was actually deleted
        try {
          await loadUsers();
          // If we get here without error, the user was likely deleted successfully
          setIsDeleteModalOpen(false);
          setUserToDelete(null);
          loadStatistics();
        } catch (refreshErr) {
          // If refresh fails, show the original error
          showNotification('Failed to delete user - please check your connection', 'error');
        }
      } else {
        showNotification(err.message || 'Failed to delete user', 'error');
      }
    } finally {
      setActionLoading(null);
    }
  };

  // Handle user activation/deactivation
  const handleToggleUserStatus = async (user: User) => {
    try {
      setActionLoading(user.id);
      const response = user.is_active 
        ? await userManagementService.deactivateUser(user.id)
        : await userManagementService.activateUser(user.id);
      
      if (response.success) {
        showNotification(
          `User ${user.is_active ? 'deactivated' : 'activated'} successfully`, 
          'success'
        );
        loadUsers();
        loadStatistics();
      } else {
        showNotification(response.error?.message || 'Failed to update user status', 'error');
      }
    } catch (err: any) {
      showNotification(err.message || 'Failed to update user status', 'error');
    } finally {
      setActionLoading(null);
    }
  };

  // Handle edit user
  const handleEditUser = (user: User) => {
    setSelectedUser(user);
    setIsEditModalOpen(true);
  };

  // Handle refresh
  const handleRefresh = () => {
    loadUsers();
    loadStatistics();
  };

  // Clear filters
  const clearFilters = () => {
    setSearchTerm('');
    setSelectedRoles([]);
    setStatusFilter('all');
    setSortBy('-created_at');
    setCurrentPage(1);
  };

  // Check if any filters are active and count them
  const { hasActiveFilters, activeFilterCount } = useMemo(() => {
    let count = 0;

    // Count search term as a filter if it exists
    if (searchTerm.trim() !== '') {
      count++;
    }

    // Count role filters
    if (selectedRoles.length > 0) {
      count++;
    }

    // Count status filter (if not 'all')
    if (statusFilter !== 'all') {
      count++;
    }

    // Count sort (if not default)
    if (sortBy !== '-created_at') {
      count++;
    }

    return {
      hasActiveFilters: count > 0,
      activeFilterCount: count,
    };
  }, [searchTerm, selectedRoles, statusFilter, sortBy]);

  // Get role color
  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin':
        return 'text-red-600 bg-red-100';
      case 'host':
        return 'text-blue-600 bg-blue-100';
      case 'guest':
        return 'text-green-600 bg-green-100';
      case 'usher':
        return 'text-purple-600 bg-purple-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };


  if (loading && users.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
      {/* Page Header */}
      <div className="mb-4 sm:mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className={`text-xl sm:text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
              User Management
            </h1>
            <p className={`mt-1 text-xs sm:text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
              Manage system users and their permissions
            </p>
          </div>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3">
            <Button
              variant="outline"
              onClick={handleRefresh}
              disabled={loading}
              leftIcon={<RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />}
              className={`w-full sm:w-auto ${
                theme === 'dark' 
                  ? 'border-gray-700 text-white hover:border-gray-600' 
                  : 'border-gray-300 text-gray-800 hover:border-gray-400'
              }`}
            >
              Refresh
            </Button>
            
            <Button
              onClick={() => setIsCreateModalOpen(true)}
              leftIcon={<Plus className="h-4 w-4" />}
              className="w-full sm:w-auto"
            >
              Add User
            </Button>
            
            <Button
              variant="outline"
              onClick={() => setIsHistoricalEventImportModalOpen(true)}
              leftIcon={<Upload className="h-4 w-4" />}
              className={`w-full sm:w-auto ${
                theme === 'dark' 
                  ? 'border-gray-700 text-white hover:border-gray-600' 
                  : 'border-gray-300 text-gray-800 hover:border-gray-400'
              }`}
            >
              Import Historical Events
            </Button>
          </div>
        </div>
      </div>

      {/* Stats Cards — aligned with dashboard style */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 max-w-6xl mx-auto px-4 sm:px-6 mb-4 sm:mb-6">
        <EnhancedLightCard
          title="Total Users"
          value={statistics?.total_users || 0}
          icon={<Users />}
          compactHorizontal
        />
        <EnhancedLightCard
          title="Admins"
          value={statistics?.admin_users || 0}
          icon={<Shield />}
          compactHorizontal
        />
        <EnhancedLightCard
          title="Hosts"
          value={statistics?.host_users || 0}
          icon={<UserPlus />}
          compactHorizontal
        />
        <EnhancedLightCard
          title="Guests"
          value={statistics?.guest_users || 0}
          icon={<UserIcon />}
          compactHorizontal
        />
        <EnhancedLightCard
          title="Ushers"
          value={statistics?.usher_users || 0}
          icon={<UserIcon />}
          compactHorizontal
        />
      </div>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h2 className={`text-2xl font-bold ${
            theme === "dark" ? "text-white" : "text-gray-900"
          }`}>
            Users
          </h2>
        </div>

        <div className="flex items-center space-x-3">
          {/* View Mode Toggle - Hidden on mobile */}
          <div className="hidden sm:block">
            <ViewToggle
              currentView={currentView}
              onViewChange={setCurrentView}
            />
          </div>
        </div>
      </div>

      {/* Search Bar - Full Width */}
      <div className="relative mb-6">
        <input
          type="text"
          placeholder="Search by user name, email, or phone..."
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
          }}
          className={`block w-full rounded-md shadow-sm focus:ring-mirsal-green focus:border-mirsal-green sm:text-sm transition-all duration-200 pl-10 pr-3 py-2.5 ${
            theme === 'dark'
              ? `border-gray-600 bg-gray-800 text-white placeholder-gray-400`
              : `border-gray-300 bg-white text-gray-900 placeholder-gray-500`
          }`}
          autoComplete="off"
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck="false"
        />
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Search
            className={`h-4 w-4 ${
              theme === 'dark' ? 'text-gray-400' : 'text-gray-400'
            }`}
          />
        </div>
      </div>

      {/* Main Layout: Users Grid + Right Sidebar */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        {/* Main Content - Users Grid/Table */}
        <div className="lg:col-span-9">
          <div className={`rounded-lg p-2 backdrop-blur-sm ${
            theme === 'dark' 
              ? 'bg-gray-900/80 border border-gray-700' 
              : 'bg-white/90 border border-gray-200'
          }`}>

            {/* Error State */}
            {error && (
              <div className={`rounded-lg p-3 sm:p-4 mb-4 sm:mb-6 ${
                theme === 'dark' 
                  ? 'bg-red-900/20 border border-red-700/50' 
                  : 'bg-red-50 border border-red-200'
              }`}>
                <div className="flex items-center">
                  <div className={theme === 'dark' ? 'text-red-400' : 'text-red-600'}>
                    <p className="text-sm sm:text-base font-medium">Error loading users</p>
                    <p className="text-xs sm:text-sm">{error}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Users Display */}
            {users.length > 0 ? (
              <>
                {/* Show grid on mobile, respect currentView on desktop */}
                <div className="block sm:hidden">
                  <UserGridView
                    users={users}
                    onEdit={handleEditUser}
                    onDelete={handleDeleteUser}
                    onToggleStatus={handleToggleUserStatus}
                    onShowDetails={handleShowGuestDetails}
                    loading={loading}
                    getRoleColor={getRoleColor}
                  />
                </div>
                
                {/* Desktop view - respect currentView */}
                <div className="hidden sm:block">
                  {currentView === 'table' && (
                    <UserTableView
                      users={users}
                      onEdit={handleEditUser}
                      onDelete={handleDeleteUser}
                      onToggleStatus={handleToggleUserStatus}
                      onShowDetails={handleShowGuestDetails}
                      loading={loading}
                    />
                  )}
                  
                  {currentView === 'grid' && (
                <UserGridView
                  users={users}
                  onEdit={handleEditUser}
                  onDelete={handleDeleteUser}
                  onToggleStatus={handleToggleUserStatus}
                  onShowDetails={handleShowGuestDetails}
                  loading={loading}
                  getRoleColor={getRoleColor}
                />
                  )}
                </div>
              </>
            ) : (
              <div className="text-center py-8 sm:py-12">
                <Users className={`mx-auto h-10 w-10 sm:h-12 sm:w-12 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-400'}`} />
                <h3 className={`mt-2 text-sm sm:text-base font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  No users found
                </h3>
                <p className={`mt-1 text-xs sm:text-sm px-4 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-500'}`}>
                  {hasActiveFilters
                    ? 'Try adjusting your filters or search terms.'
                    : 'Get started by creating a new user.'
                  }
                </p>
                {!hasActiveFilters && (
                  <div className="mt-4 sm:mt-6">
                    <Button onClick={() => setIsCreateModalOpen(true)} className="w-full sm:w-auto">
                      <Plus className="h-4 w-4 mr-2" />
                      Add User
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                  totalCount={totalCount}
                  pageSize={pageSize}
                />
              </div>
            )}
          </div>
        </div>

        {/* Right Sidebar - Filters & Sort Card */}
        <div className="lg:col-span-3">
          <div className="sticky top-6 space-y-4">
            <div className={`rounded-lg border-2 p-4 ${
              theme === "dark" 
                ? "bg-gray-800 border-gray-700" 
                : "bg-white border-gray-200"
            }`}>
              <div className="flex items-center justify-between mb-4">
                <h3 className={`text-lg font-semibold ${
                  theme === "dark" ? "text-white" : "text-gray-900"
                }`}>
                  Filters & Sort
                </h3>
                {hasActiveFilters && (
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    theme === "dark" 
                      ? "bg-mirsal-green/20 text-mirsal-green" 
                      : "bg-mirsal-green/10 text-mirsal-green"
                  }`}>
                    {activeFilterCount} active
                  </span>
                )}
              </div>

              <div className="space-y-4">
                {/* Sort Dropdown */}
                <div>
                  <Select
                    label="Sort by"
                    options={[
                      { value: '-created_at', label: 'Newest First' },
                      { value: 'created_at', label: 'Oldest First' },
                      { value: 'first_name', label: 'First Name A-Z' },
                      { value: '-first_name', label: 'First Name Z-A' },
                      { value: 'last_name', label: 'Last Name A-Z' },
                      { value: '-last_name', label: 'Last Name Z-A' }
                    ]}
                    value={sortBy}
                    onChange={(value) => setSortBy(value)}
                    placeholder="Sort by..."
                  />
                </div>

                {/* Role Filter - Multi-select */}
                <div>
                  <Select
                    multiple
                    label="Role"
                    options={[
                      { value: 'admin', label: 'Admin' },
                      { value: 'host', label: 'Host' },
                      { value: 'guest', label: 'Guest' },
                      { value: 'usher', label: 'Usher' }
                    ]}
                    value={selectedRoles}
                    onChange={(values) => setSelectedRoles(values)}
                    placeholder="All Roles"
                  />
                </div>

                {/* Status Filter */}
                <div>
                  <Select
                    label="Status"
                    options={[
                      { value: 'all', label: 'All Status' },
                      { value: 'active', label: 'Active' },
                      { value: 'inactive', label: 'Inactive' }
                    ]}
                    value={statusFilter}
                    onChange={(value) => setStatusFilter(value as 'all' | 'active' | 'inactive')}
                    placeholder="All Status"
                  />
                </div>

                {/* Clear Filters */}
                <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearFilters}
                    disabled={!hasActiveFilters}
                    className={`w-full transition-all duration-200 font-medium ${
                      hasActiveFilters
                        ? "text-mirsal-green hover:text-mirsal-green-dark hover:bg-mirsal-green/10"
                        : "text-gray-400 dark:text-gray-500 cursor-not-allowed"
                    }`}
                  >
                    Clear all filters
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Create User Modal */}
      <Modal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        title="Create New User"
        size="lg"
      >
        <UserForm
          onSubmit={handleCreateUser}
          onCancel={() => {
            setIsCreateModalOpen(false);
            setServerErrors({});
          }}
          loading={actionLoading === 'create'}
          serverErrors={serverErrors}
        />
      </Modal>

      {/* Edit User Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedUser(null);
        }}
        title="Edit User"
        size="lg"
      >
        {selectedUser && (
          <UserForm
            user={selectedUser}
            onSubmit={handleUpdateUser}
            onCancel={() => {
              setIsEditModalOpen(false);
              setSelectedUser(null);
              setServerErrors({});
            }}
            loading={actionLoading === 'update'}
            isEdit
            serverErrors={serverErrors}
          />
        )}
      </Modal>

      {/* Bulk Upload Modal */}
      <BulkUserUpload
        isOpen={isBulkUploadModalOpen}
        onClose={() => setIsBulkUploadModalOpen(false)}
        onSuccess={() => {
          loadUsers();
          loadStatistics();
        }}
      />

      {/* Historical Events Import Modal */}
      <HistoricalEventImport
        isOpen={isHistoricalEventImportModalOpen}
        onClose={() => setIsHistoricalEventImportModalOpen(false)}
        onSuccess={() => {
          loadUsers();
          loadStatistics();
        }}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmDeleteModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setUserToDelete(null);
        }}
        onConfirm={confirmDeleteUser}
        title="Delete User"
        message="Are you sure you want to permanently delete this user? This action cannot be undone and will remove all user data from the system."
        itemName={userToDelete ? `${formatFullNameWithTitle(userToDelete.title, userToDelete.first_name, userToDelete.last_name)} (${userToDelete.email || userToDelete.phone})` : ''}
        itemType="User"
        loading={actionLoading === userToDelete?.id}
        destructive={true}
      />

      {/* Restore User Confirmation Modal */}
      <RestoreUserModal
        isOpen={isRestoreModalOpen}
        onClose={() => {
          setIsRestoreModalOpen(false);
          setDeletedUserInfo(null);
          setPendingUserData(null);
        }}
        onConfirm={async () => {
          if (pendingUserData) {
            try {
              setActionLoading('restore');
              const response = await userManagementService.createUser(pendingUserData, true);
              
              if (response.success) {
                showNotification('User restored and updated successfully', 'success');
                setIsRestoreModalOpen(false);
                setIsCreateModalOpen(false);
                setServerErrors({});
                setDeletedUserInfo(null);
                setPendingUserData(null);
                loadUsers();
                loadStatistics();
              } else {
                showNotification(response.error?.message || 'Failed to restore user', 'error');
              }
            } catch (err: any) {
              showNotification(err.message || 'Failed to restore user', 'error');
            } finally {
              setActionLoading(null);
            }
          }
        }}
        deletedUser={deletedUserInfo}
        loading={actionLoading === 'restore'}
      />

      {/* Guest Details Modal */}
      <GuestDetailsModal
        isOpen={isGuestDetailsModalOpen}
        onClose={() => {
          setIsGuestDetailsModalOpen(false);
          setGuestForDetails(null);
        }}
        guestId={guestForDetails?.id || ''}
        guestName={guestForDetails ? formatFullNameWithTitle(guestForDetails.title, guestForDetails.first_name, guestForDetails.last_name) : ''}
      />
    </div>
  );
};

export default UserManagementPage;

